import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

import {
  Row,
  Col,
  Form,
  Nav,
  Card,
  Spinner,
  Container,
  Table,
  Button,
} from "react-bootstrap";

import Chart from "react-apexcharts";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { ADMIN_PLATFORM_ANALYTICS_STUDENT_PAYMENTS_API } from "../../../api/api";

import { FaArrowTrendUp, FaRegStar } from "react-icons/fa6";
import { FiTarget } from "react-icons/fi";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

// import { IoIosStar } from "react-icons/io";

import enrolledStudentsIcon from "../../../assets/enrolled-students.png";
import courseCompletedIcon from "../../../assets/course-completed.png";
import progressIcon from "../../../assets/in-progress.png";
import performanceIcon from "../../../assets/average-performance.png";

import assignment from "../../../assets/assignment-completion-rate.png";
import clock from "../../../assets/average-learning-hours.png";
import graduation from "../../../assets/course-completion-rate.png";
import star from "../../../assets/student-satisfaction.png";

// import platformIcon from "../../../assets/platformIcon.png";
// import tutorIcon from "../../../assets/tutorIcon.png";

// import totalViewsImg from "../../../assets/tutor-student-dou-icon1.png";
// import avgCompletionImg from "../../../assets/tutor-student-dou-icon1.png";
// import totalstudentImg from "../../../assets/tutor-student-dou-icon1.png";
// import AvgRatingImg from "../../../assets/tutor-student-dou-icon1.png";

// latestIcons
import totalStudents from "../../../assets/icons/admin/enrolled-students.svg";
import activeTutors from "../../../assets/icons/admin/active-tutors.svg";
import activeCourses from "../../../assets/icons/student/course.svg";
import totalRevenue from "../../../assets/icons/tutor/total-earnings.svg";

import excelImg from "../../../assets/excel-icon.png";
import Nofound from "../../../assets/not-found.png";

import { getPageNumbers } from "../../../utils/pagination";
import { exportToExcel } from "../../../utils/exportExcel";
import { handleApiError } from "../../../utils/apiErrorHandler";
import { useSortableTable } from "../../../hooks/useSortableTable";

import "./AdminPlatformAnalytics.css";

const AdminPlatformAnalytics = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Overview");

  const [platformAnalyticsStats, setPlatformAnalyticsStats] = useState({});
  const [studentMetrics, setStudentMetrics] = useState({});
  const [studentCourseProgress, setStudentCourseProgress] = useState([]);
  const [engagementMetrics, setEngagementMetrics] = useState({});
  const [chartData, setChartData] = useState({});

  const [currentPage, setCurrentPage] = useState(1);

  // Filter states
  const [selectedChartCourse, setSelectedChartCourse] = useState("");
  const [paymentCourseFilter, setPaymentCourseFilter] = useState("All Courses");
  const [selectedStudentFilter, setSelectedStudentFilter] =
    useState("All Students");
  const [selectedCourseCompletion, setSelectedCourseCompletion] =
    useState("All Status");

  //loading
  const [loadingPlatformAnalytics, setLoadingPlatformAnalytics] =
    useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setLoadingPlatformAnalytics(false);
      navigate("/admin-signin");

      return;
    }

    const fetchPlatformAnalytics = async () => {
      try {
        const response = await axios.get(
          ADMIN_PLATFORM_ANALYTICS_STUDENT_PAYMENTS_API.GET_ANALYTICS_ADMIN_DASHBOARD,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          setPlatformAnalyticsStats(data.data.overview || {});
          setStudentMetrics(data.data.studentMetrics || {});
          setStudentCourseProgress(data.data.studentcourseprogress || []);
          setEngagementMetrics(data.data.engagementMetrics || {});

          setChartData(data.data.charts || {});

          setSelectedChartCourse(
            data.data.charts?.ratingDistribution?.[0]?.course_id || "",
          );
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error analytics:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/admin-signin");
      } finally {
        setLoadingPlatformAnalytics(false);
      }
    };

    fetchPlatformAnalytics();
  }, [navigate]);

  const filteredCoursesForDropdown = useMemo(() => {
    return [...new Set(studentCourseProgress.map((s) => s.course_title))];
  }, [studentCourseProgress]);

  const filteredStudentsForDropdown = useMemo(() => {
    return [...new Set(studentCourseProgress.map((s) => s.full_name))];
  }, [studentCourseProgress]);

  const filteredCourseCompletionDropdown = useMemo(() => {
    return [...new Set(studentCourseProgress.map((s) => s.course_completion))];
  }, [studentCourseProgress]);

  const filteredStudentCourseProgress = useMemo(() => {
    return studentCourseProgress.filter((s) => {
      const matchCourse =
        paymentCourseFilter === "All Courses" ||
        s.course_title === paymentCourseFilter;

      const matchStudent =
        selectedStudentFilter === "All Students" ||
        s.full_name === selectedStudentFilter;

      const matchStatus =
        selectedCourseCompletion === "All Status" ||
        s.course_completion === selectedCourseCompletion;

      return matchCourse && matchStudent && matchStatus;
    });
  }, [
    studentCourseProgress,
    paymentCourseFilter,
    selectedStudentFilter,
    selectedCourseCompletion,
  ]);

  // SORT HOOK (MAIN ADDITION)
  const { sortedData, sortConfig, handleSort } = useSortableTable(
    filteredStudentCourseProgress,
  );

  const clearFiltersStudent = () => {
    setPaymentCourseFilter("All Courses");
    setSelectedStudentFilter("All Students");
    setSelectedCourseCompletion("All Status");
    setCurrentPage(1);
  };

  const handleExportStudentCourseProgress = () => {
    const formattedData = (sortedData || []).map((a) => ({
      Register_ID: a.student_reg_number || "N/A",
      Name: a.full_name || "N/A",
      Email: a.email || "N/A",
      Phone: a.phone_number || "N/A",
      Course: a.course_title || "N/A",
      "Course Progress": a.course_completion || "N/A",
      "Certificate Status": a.certificate_status || "N/A",
      "Internship Status": a.internship_status || "N/A",
    }));

    exportToExcel({
      data: formattedData,
      fileName: "Student_Course_Progress",
      sheetName: "Course_Progress",
    });
  };

  // LINE CHART
  const growthCategories =
    chartData.platformGrowth?.map((item) => item.month) || [];

  const growthOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: growthCategories,
    },
    colors: ["#136AD0", "#F59E0B", "#8B5CF6"],
    legend: {
      position: "bottom",
    },
  };

  const growthSeries = [
    {
      name: "Students",
      data:
        chartData.platformGrowth?.map((item) => Number(item.students)) || [],
    },
    {
      name: "Tutors",
      data: chartData.platformGrowth?.map((item) => Number(item.tutors)) || [],
    },
    {
      name: "Courses",
      data: chartData.platformGrowth?.map((item) => Number(item.courses)) || [],
    },
  ];

  //  BAR CHART
  const courseCategories =
    chartData.coursePerformance?.map((item) => item.course_title) || [];

  const courseOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
        columnWidth: "45%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: courseCategories,
      labels: {
        style: {
          fontSize: "11px",
        },
      },
    },
    colors: ["#136AD0", "#10B981"],
    legend: {
      position: "bottom",
    },
  };

  const courseSeries = [
    {
      name: "Enrolled",
      data:
        chartData.coursePerformance?.map((item) => Number(item.enrolled)) || [],
    },
    {
      name: "Completed",
      data:
        chartData.coursePerformance?.map((item) => Number(item.completed)) ||
        [],
    },
  ];

  // HORIZONTAL BAR
  const courseOptionsDropdown = chartData.ratingDistribution || [];

  const selectedCourse =
    courseOptionsDropdown.find(
      (c) => String(c.course_id) === String(selectedChartCourse),
    ) ||
    courseOptionsDropdown[0] ||
    {};

  const ratingOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        distributed: true,
        barHeight: "60%",

        borderRadius: 4,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: ["5 Stars", "4 Stars", "3 Stars", "2 Stars", "1 Star"],
    },
    colors: [
      "#2563EB", // 5 Stars
      "#6366F1", // 4 Stars
      "#F59E0B", // 3 Stars
      "#10B981", // 2 Stars
      "#EF4444", // 1 Star
    ],
    legend: {
      show: false,
    },
    grid: {
      strokeDashArray: 4,
    },
  };

  const ratingSeries = [
    {
      name: "Ratings",
      data: selectedCourse
        ? [
            Number(selectedCourse.five_star) || 0,
            Number(selectedCourse.four_star) || 0,
            Number(selectedCourse.three_star) || 0,
            Number(selectedCourse.two_star) || 0,
            Number(selectedCourse.one_star) || 0,
          ]
        : [0, 0, 0, 0, 0],
    },
  ];

  /* Student Metrics */
  const completionCategories =
    chartData.completionTrend?.map((item) => item.month) || [];

  const completionOptions = {
    chart: { type: "line", toolbar: { show: false } },
    stroke: { curve: "smooth", width: 3 },
    markers: { size: 4 },
    xaxis: { categories: completionCategories },
    colors: ["#22C55E", "#F59E0B"],
    legend: { position: "bottom" },
  };

  const completionSeries = [
    {
      name: "Completed",
      data:
        chartData.completionTrend?.map((item) => Number(item.completed)) || [],
    },
    {
      name: "In Progress",
      data:
        chartData.completionTrend?.map((item) => Number(item.in_progress)) ||
        [],
    },
  ];

  const scoreCategories =
    chartData.courseAvgScores?.map((item) => item.course_title) || [];

  const courseScoreOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        borderRadiusApplication: "all", // instead of "end"
        columnWidth: "45%",
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: scoreCategories,
      labels: { style: { fontSize: "12px" } },
    },
    colors: ["#2563EB"],
    grid: { strokeDashArray: 4 },
  };

  const courseScoreSeries = [
    {
      name: "Avg Score",
      data:
        chartData.courseAvgScores?.map((item) => Number(item.avg_score)) || [],
    },
  ];

  // Pagination slice
  const rowsPerPage = 5;
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const indexOfLastStudent = currentPage * rowsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - rowsPerPage;

  const currentStudentCourseProgress = sortedData.slice(
    indexOfFirstStudent,
    indexOfLastStudent,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [paymentCourseFilter, selectedStudentFilter, selectedCourseCompletion]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [totalPages, currentPage]);

  //  SORT ICON HELPER
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort size={11} />;
    if (sortConfig.direction === "asc") return <FaSortUp size={11} />;
    return <FaSortDown size={11} />;
  };

  // function call
  const pages = getPageNumbers(currentPage, totalPages);

  if (loadingPlatformAnalytics) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // const completionTrendSeries = [
  //   {
  //     name: "Completed",
  //     data: [100, 120, 150, 175, 200],
  //   },
  //   {
  //     name: "In Progress",
  //     data: [60, 80, 105, 125, 150],
  //   },
  // ];

  // const completionTrendOptions = {
  //   chart: {
  //     type: "line",
  //     toolbar: { show: false },
  //     zoom: { enabled: false },
  //   },

  //   stroke: {
  //     curve: "smooth",
  //     width: 3,
  //   },

  //   markers: {
  //     size: 5,
  //     strokeWidth: 2,
  //     hover: { size: 7 },
  //   },

  //   colors: ["#22C55E", "#F59E0B"],

  //   xaxis: {
  //     categories: ["Jun", "Jul", "Aug", "Sep", "Oct"],
  //     axisBorder: { show: false },
  //     axisTicks: { show: false },
  //   },

  //   yaxis: {
  //     labels: {
  //       style: { colors: "#6B7280" },
  //     },
  //   },

  //   grid: {
  //     borderColor: "#E5E7EB",
  //     strokeDashArray: 4,
  //   },

  //   tooltip: {
  //     shared: true,
  //     intersect: false,
  //     theme: "light",
  //   },

  //   legend: {
  //     position: "bottom",
  //     horizontalAlign: "center",
  //     markers: {
  //       radius: 12,
  //     },
  //   },
  // };

  /* Revenue Pie */
  // const revenueOptions = {
  //   labels: ["Platform Share", "Tutor Share"],
  //   colors: ["#2563EB", "#6366F1"],
  //   legend: {
  //     show: false,
  //   },
  //   dataLabels: {
  //     enabled: true,
  //     formatter: (val) => `${Math.round(val)}%`,
  //     style: {
  //       fontSize: "13px",
  //       fontWeight: 600,
  //       colors: ["#ffffff"],
  //     },
  //   },
  //   plotOptions: {
  //     pie: {
  //       expandOnClick: false,
  //     },
  //   },
  // };

  // const revenueSeries = [60, 40];

  /* Payment Model */
  // const paymentOptions = {
  //   labels: ["One-Time", "Royalty"],
  //   colors: ["#2563EB", "#F59E0B"],
  //   legend: { show: false },
  //   dataLabels: {
  //     enabled: true,
  //     formatter: (val) => `${Math.round(val)}%`,
  //     style: {
  //       fontSize: "13px",
  //       fontWeight: 600,
  //       colors: ["#fff"],
  //     },
  //   },
  // };

  // const paymentSeries = [65, 35];

  // performance code

  return (
    <Container className="admin-platform-analytics-wrapper">
      <h4 className="admin-dashboard-title">Platform Analytics</h4>
      <p className="admin-dashboard-subtitle">
        View key platform metrics, including students, tutors, courses, and
        enrollments.
      </p>

      <Row className="admin-analytics-section-cards-row">
        <Col lg={3} md={4} sm={6} xs={12} className="mb-3">
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Enrolled Students
            </h6>
            <img
              src={totalStudents}
              className="admin-analytics-stat-icon"
              alt="students"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(platformAnalyticsStats.total_students) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              158 Active • 37 Completed
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Active Tutors
            </h6>
            <img
              src={activeTutors}
              className="admin-analytics-stat-icon"
              alt="tutors"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(platformAnalyticsStats.active_tutors) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              Avg Rating: 4.6
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Total Courses
            </h6>
            <img
              src={activeCourses}
              className="admin-analytics-stat-icon"
              alt="course"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(platformAnalyticsStats.total_courses) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              38 Published • 7 Draft
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Total Revenue
            </h6>
            <img
              src={totalRevenue}
              className="admin-analytics-stat-icon"
              alt="revenue"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(platformAnalyticsStats.total_revenue) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              $58K this month
            </span>
          </div>
        </Col>
      </Row>

      {/*  TABS  */}
      <Row className="mb-4">
        <Col lg={12}>
          <Nav variant="tabs" className="admin-platform-analytics-tabs">
            {[
              "Overview",
              "Student Metrics",
              // "Tutor Metrics",
              // "Revenue Analytics",
              "Performance",
            ].map((tab) => (
              <Nav.Item key={tab}>
                <Nav.Link
                  active={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Col>
      </Row>

      {activeTab === "Overview" && (
        <>
          {/*  LINE CHART  */}
          <Card className="admin-platform-analytics-card mb-4">
            <h6 className="admin-platform-analytics-title">
              <span className="admin-platform-analytics-icon blue">
                <FaArrowTrendUp />
              </span>
              Platform Growth Trend
            </h6>
            <Chart
              options={growthOptions}
              series={growthSeries}
              type="area"
              height={300}
            />
          </Card>

          {/*  BOTTOM SECTION  */}
          <Row>
            <Col lg={7} className="admin-platform-analytics-course-performance">
              <Card className="admin-platform-analytics-card">
                <h6 className="admin-platform-analytics-title">
                  <span className="admin-platform-analytics-icon blue">
                    <FiTarget />
                  </span>
                  Course Performance
                </h6>
                <Chart
                  options={courseOptions}
                  series={courseSeries}
                  type="bar"
                  height={260}
                />
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="admin-platform-analytics-card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="admin-platform-analytics-title">
                    <span className="admin-platform-analytics-icon yellow">
                      <FaRegStar />
                    </span>
                    Course Ratings Distribution
                  </h5>

                  <div className="d-flex">
                    {/* Dropdown for courses */}
                    <Form.Select
                      size="sm"
                      value={selectedChartCourse}
                      onChange={(e) => setSelectedChartCourse(e.target.value)}
                      className="admin-platform-analytics-filter-select"
                    >
                      {courseOptionsDropdown.length > 0 ? (
                        courseOptionsDropdown.map((course) => (
                          <option
                            key={course.course_id}
                            value={course.course_id}
                          >
                            {course.course_title}
                          </option>
                        ))
                      ) : (
                        <option>No data available</option>
                      )}
                    </Form.Select>
                  </div>
                </div>
                <Chart
                  options={ratingOptions}
                  series={ratingSeries}
                  type="bar"
                  height={260}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      {activeTab === "Student Metrics" && (
        <>
          <Row className="mb-5">
            <Col lg={7}>
              <Card className="admin-platform-analytics-card">
                <h6 className="admin-platform-analytics-title">
                  Completion Trend
                </h6>
                <Chart
                  options={completionOptions}
                  series={completionSeries}
                  height={260}
                />
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="admin-platform-analytics-card">
                <h6 className="admin-platform-analytics-title">
                  Student Statistics
                </h6>

                <div className="admin-platform-analytics-stat purple">
                  <div>
                    <span>Total Courses Purchased</span>
                    <strong>
                      {Number(studentMetrics.total_course_purchase) || "-"}
                    </strong>
                  </div>
                  <img
                    src={performanceIcon}
                    alt="performance"
                    className="admin-platform-analytics-stat-icon"
                  />
                </div>

                <div className="admin-platform-analytics-stat blue">
                  <div>
                    <span>Enrolled Students</span>
                    <strong>
                      {Number(studentMetrics.enrolled_students) || "-"}
                    </strong>
                  </div>
                  <img
                    src={enrolledStudentsIcon}
                    alt="students"
                    className="admin-platform-analytics-stat-icon"
                  />
                </div>

                <div className="admin-platform-analytics-stat green">
                  <div>
                    <span>Course Completions</span>
                    <strong>
                      {Number(studentMetrics.completed_students) || "-"}
                    </strong>
                  </div>
                  <img
                    src={courseCompletedIcon}
                    alt="completed"
                    className="admin-platform-analytics-stat-icon"
                  />
                </div>

                <div className="admin-platform-analytics-stat orange">
                  <div>
                    <span>In Progress</span>
                    <strong>
                      {Number(studentMetrics.in_progress_students) || "-"}
                    </strong>
                  </div>
                  <img
                    src={progressIcon}
                    alt="progress"
                    className="admin-platform-analytics-stat-icon"
                  />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Top Tutors Table */}
          <Card className="admin-platform-analytics-tutor-table-card">
            <Card.Body className="p-0">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="admin-platform-analytics-table-title">
                  Student Course Progress
                </h5>

                <div className="d-flex gap-3">
                  <Form.Select
                    // size="sm"
                    value={paymentCourseFilter}
                    onChange={(e) => setPaymentCourseFilter(e.target.value)}
                    className="tutor-analytics-section-student-filter tutor-analytics-section-topcourses-filter"
                  >
                    <option value="All Courses">All Courses</option>
                    {filteredCoursesForDropdown.map((course, index) => (
                      <option key={index} value={course}>
                        {course}
                      </option>
                    ))}
                  </Form.Select>

                  <Form.Select
                    // size="sm"
                    value={selectedStudentFilter}
                    onChange={(e) => setSelectedStudentFilter(e.target.value)}
                    className="tutor-analytics-section-student-filter tutor-analytics-section-topcourses-filter"
                  >
                    <option value="All Students">All Students</option>
                    {filteredStudentsForDropdown.map((student, index) => (
                      <option key={index} value={student}>
                        {student}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Select
                    // size="sm"
                    value={selectedCourseCompletion}
                    onChange={(e) =>
                      setSelectedCourseCompletion(e.target.value)
                    }
                    className="tutor-analytics-section-student-filter tutor-analytics-section-topcourses-filter"
                  >
                    <option value="All Payments">All Status</option>
                    {filteredCourseCompletionDropdown.map((status, index) => (
                      <option key={index} value={status}>
                        {status}
                      </option>
                    ))}
                  </Form.Select>

                  <Button
                    // size="sm"
                    className="tutor-analytics-section-student-export"
                    onClick={clearFiltersStudent}
                  >
                    Clear
                  </Button>

                  <Button
                    size="sm"
                    className="admin-platform-analytics-table-export me-2"
                    onClick={handleExportStudentCourseProgress}
                    disabled={sortedData.length === 0}
                    title={
                      sortedData.length === 0
                        ? "No data available to export"
                        : "Export to Excel"
                    }
                    style={{
                      cursor:
                        sortedData.length === 0 ? "not-allowed" : "pointer",
                      opacity: sortedData.length === 0 ? 0.5 : 1,
                    }}
                  >
                    <img
                      src={excelImg}
                      alt="Excel"
                      className="admin-dashboard-table-export-excel-img"
                      style={{ marginBottom: 0 }}
                    />
                  </Button>
                </div>
              </div>

              {/* Table Header */}
              <div className="admin-assignment-management-table-wrapper">
                <Table className="admin-assignment-management-table">
                  <thead>
                    <tr>
                      <th
                        onClick={() => handleSort("student_reg_number")}
                        style={{ cursor: "pointer" }}
                      >
                        Enroll Id {renderSortIcon("student_reg_number")}
                      </th>
                      <th
                        onClick={() => handleSort("full_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="admin-dashboard-sort-header">
                          Student {renderSortIcon("full_name")}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort("email")}
                        style={{ cursor: "pointer" }}
                      >
                        Email {renderSortIcon("email")}
                      </th>
                      <th>Phone</th>
                      <th
                        onClick={() => handleSort("course_title")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="admin-dashboard-sort-header">
                          Course {renderSortIcon("course_title")}{" "}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort("course_completion")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="admin-dashboard-sort-header">
                          Course Progress
                          {renderSortIcon("course_completion")}
                        </div>
                      </th>
                      <th>
                        <div className="admin-dashboard-sort-header">
                          Certificate Status{" "}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort("internship_status")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="admin-dashboard-sort-header">
                          Internship Status{" "}
                          {renderSortIcon("internship_status")}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentStudentCourseProgress.length > 0 ? (
                      currentStudentCourseProgress.map((student) => (
                        <tr key={student.student_course_id}>
                          <td title={student.student_reg_number}>
                            {student.student_reg_number || "-"}
                          </td>
                          <td title={student.full_name}>
                            {student.full_name || "-"}
                          </td>
                          <td title={student.email}>{student.email || "-"}</td>
                          <td title={student.phone_number}>
                            {student.phone_number || "-"}
                          </td>
                          <td title={student.course_title}>
                            {student.course_title || "-"}
                          </td>
                          <td title={student.course_completion}>
                            <span
                              className={`admin-platform-course-progress-status ${student.course_completion
                                ?.toLowerCase()
                                .replace(/\s+/g, "-")}`}
                            >
                              {student.course_completion || "-"}
                            </span>
                          </td>
                          <td title={student.certificate_status}>
                            {student.certificate_status || "-"}
                          </td>
                          <td title={student.internship_status}>
                            <span
                              className={`admin-platform-course-internship-status ${student.internship_status?.toLowerCase()}`}
                            >
                              {student.internship_status || "-"}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" style={{ textAlign: "center" }}>
                          <img
                            src={Nofound}
                            alt="No Data Found"
                            className="admin-all-tutor-empty-img"
                          />
                          <p className="admin-dashboard-no-data">
                            {" "}
                            No Data Found
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="tutor-assignment-table-pagination">
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>

              {pages.map((page, idx) =>
                page === "..." ? (
                  <span key={idx} style={{ padding: "0 3px" }}>
                    ...
                  </span>
                ) : (
                  <Button
                    key={idx}
                    active={currentPage === page}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Button>
                ),
              )}

              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* {activeTab === "Tutor Metrics" && (
        <>
          <Row className="tutor-metrics-row mb-4">
            Metric Cards
            <Col md={4}>
              <Card className="admin-platform-analytics-metric-card success">
                <Card.Body>
                  <h3 className="admin-platform-analytics-metric-value">195</h3>
                  <p className="admin-platform-analytics-metric-label">Active Tutors</p>
                  <span className="admin-platform-analytics-metric-badge admin-platform-analytics-success-badge">
                    15% Increase
                  </span>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="admin-platform-analytics-metric-card info">
                <Card.Body>
                  <h3 className="admin-platform-analytics-metric-value">
                    4.6 <IoIosStar />
                  </h3>
                  <p className="admin-platform-analytics-metric-label">Average Rating</p>
                  <span className="admin-platform-analytics-metric-badge admin-platform-analytics-info-badge">Excellent</span>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="admin-platform-analytics-metric-card warning">
                <Card.Body>
                  <h3 className="admin-platform-analytics-metric-value">$27.6K</h3>
                  <p className="admin-platform-analytics-metric-label">Total Payout</p>
                  <span className="admin-platform-analytics-metric-badge admin-platform-analytics-warning-badge">
                    $5.8K this month
                  </span>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          Top Tutors Table
          <Card className="admin-platform-analytics-tutor-table-card">
            <Card.Body className="p-0">
              <h5 className="admin-platform-analytics-table-title">
                Top Performing Tutors
              </h5>

              Table Header
              <Table className="admin-platform-analytics-tutor-table admin-platform-analytics-tutor-table-header">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Courses</th>
                    <th>Students</th>
                    <th>Rating</th>
                    <th>One-Time Pay</th>
                    <th>Royalty Pay</th>
                    <th>Total Pay</th>
                  </tr>
                </thead>
              </Table>

              Scrollable Body
              <div className="admin-platform-analytics-tutor-table-wrapper">
                <Table className="admin-platform-analytics-tutor-table">
                  <tbody>
                    {tutorData.map((tutor, index) => (
                      <tr key={index}>
                        <td>{tutor.name}</td>
                        <td>{tutor.courses}</td>
                        <td>{tutor.students}</td>
                        <td>{tutor.rating} ⭐</td>
                        <td>{tutor.oneTimePay}</td>
                        <td>{tutor.royaltyPay}</td>
                        <td className="fw-bold">{tutor.totalPay}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )} */}

      {/* {activeTab === "Revenue Analytics" && (
        <>
          <Row className="mb-4">
            <Col lg={6}>
              <Card className="admin-platform-analytics-card">
                <h6 className="admin-platform-analytics-title">
                  Completion Trend
                </h6>

                <div className="admin-platform-analytics-chart">
                  <Chart
                    options={completionTrendOptions}
                    series={completionTrendSeries}
                    type="line"
                    height={260}
                  />
                </div>
              </Card>
            </Col>

            <Col lg={6}>
              <Card className="admin-platform-analytics-card text-center">
                <h6 className="admin-platform-analytics-title">
                  Revenue Distribution
                </h6>

                <Chart
                  options={revenueOptions}
                  series={revenueSeries}
                  type="pie"
                  height={220}
                />

                <div className="admin-platform-analytics-revenue-wrapper">
                  <div className="admin-platform-analytics-revenue-box platform">
                    <div className="admin-platform-analytics-revenue-left">
                      <span className="admin-platform-analytics-revenue-label">
                        Platform Share
                      </span>
                      <strong className="admin-platform-analytics-revenue-amount">
                        $41.4K
                      </strong>
                      <span className="admin-platform-analytics-revenue-label-1">
                        60% of total
                      </span>
                    </div>

                    <img
                      src={platformIcon}
                      alt="platform"
                      className="admin-platform-analytics-revenue-icon"
                    />
                  </div>

                  <div className="admin-platform-analytics-revenue-box tutor">
                    <div className="admin-platform-analytics-revenue-left">
                      <span className="admin-platform-analytics-revenue-label">
                        Tutor Share
                      </span>
                      <strong className="admin-platform-analytics-revenue-amount">
                        $27.6K
                      </strong>
                      <span className="admin-platform-analytics-revenue-label-1">
                        40% of total
                      </span>
                    </div>

                    <img
                      src={tutorIcon}
                      alt="tutor"
                      className="admin-platform-analytics-revenue-icon"
                    />
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          <Card className="admin-platform-analytics-card">
            <h6 className="admin-platform-analytics-title">
              Payment Model Distribution
            </h6>

            <Row className="align-items-center">
              PIE CHART
              <Col lg={5}>
                <Chart
                  options={paymentOptions}
                  series={paymentSeries}
                  type="pie"
                  height={220}
                />
              </Col>

              PAYMENT INFO
              <Col lg={7}>
                <div className="admin-platform-payment-wrapper">
                  <div className="admin-platform-payment-box one-time">
                    <div className="admin-platform-payment-left">
                      <span className="admin-platform-payment-title">
                        One-Time Settlement
                      </span>
                      <strong className="admin-platform-payment-amount">
                        $45,000
                      </strong>
                      <span className="admin-platform-payment-sub">
                        Fixed payment model
                      </span>
                    </div>

                    <span className="admin-platform-payment-badge blue">
                      65%
                    </span>
                  </div>

                  <div className="admin-platform-payment-box royalty">
                    <div className="admin-platform-payment-left">
                      <span className="admin-platform-payment-title">
                        Royalty-Based
                      </span>
                      <strong className="admin-platform-payment-amount">
                        $24,000
                      </strong>
                      <span className="admin-platform-payment-sub">
                        Percentage per enrollment
                      </span>
                    </div>

                    <span className="admin-platform-payment-badge orange">
                      35%
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </>
      )} */}

      {activeTab === "Performance" && (
        <>
          <Row>
            <Col lg={7}>
              <Card className="admin-platform-performance-analytics-card">
                <h6 className="admin-platform-performance-analytics-card-title">
                  Course Average Final Assignment Score
                </h6>

                <Chart
                  options={courseScoreOptions}
                  series={courseScoreSeries}
                  type="bar"
                  height={260}
                />
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="admin-platform-performance-analytics-card">
                <h6 className="admin-platform-performance-analytics-card-title">
                  Engagement Metrics
                </h6>

                {/* Metric Item 1 */}
                <div className="admin-platform-performance-analytics-metric-item blue">
                  <div className="admin-platform-performance-analytics-metric-left">
                    <span>Avg Final Assignments Score</span>
                    <strong>
                      {engagementMetrics.avg_assignment_score || 0}
                    </strong>
                  </div>
                  <img
                    src={assignment}
                    alt="Assignment"
                    className="admin-platform-performance-analytics-metric-icon"
                  />
                </div>

                {/* Metric Item 2 */}
                <div className="admin-platform-performance-analytics-metric-item green">
                  <div className="admin-platform-performance-analytics-metric-left">
                    <span>Avg Learning Hours / Total Learning Hours</span>
                    <strong>
                      {engagementMetrics.avg_learning_hours || 0} /{" "}
                      {engagementMetrics.total_learning_hours || 0}
                    </strong>
                  </div>
                  <img
                    src={clock}
                    alt="Clock"
                    className="admin-platform-performance-analytics-metric-icon"
                  />
                </div>

                {/* Metric Item 3 */}
                <div className="admin-platform-performance-analytics-metric-item orange">
                  <div className="admin-platform-performance-analytics-metric-left">
                    <span>Course Completion Rate</span>
                    <strong>{engagementMetrics.completion_rate || 0}%</strong>
                  </div>
                  <img
                    src={graduation}
                    alt="Graduation"
                    className="admin-platform-performance-analytics-metric-icon"
                  />
                </div>

                {/* Metric Item 4 */}
                <div className="admin-platform-performance-analytics-metric-item purple">
                  <div className="admin-platform-performance-analytics-metric-left">
                    <span>Student Satisfaction</span>
                    <strong>
                      {engagementMetrics.student_satisfaction || 0} ★
                    </strong>
                  </div>
                  <img
                    src={star}
                    alt="Star"
                    className="admin-platform-performance-analytics-metric-icon"
                  />
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default AdminPlatformAnalytics;
